/* =========================================================================
   diamond-feed.js — shared engine for the SHANNON loose-diamond feed.

   Live build: data comes from the Nivoda proxy (see diamond-proxy/), which
   already maps each stone into the shape this engine expects. This file owns
   the ordinal scales/formatters, the dual-handle range slider, the filter
   drawer UI, the quick-view drawer, and the fetch layer that turns filter
   state into a proxy query. The two view controllers (grid + table) drive it.

   Config is injected from the section via window.DIAMOND_FEED_CONFIG:
     { proxyUrl, shapeIcons:{id:url}, defaults:{...}, onAdd(stone) }
========================================================================= */
window.DiamondFeed = (function () {
  'use strict';

  /* ---- Section-injected config ---------------------------------------- */
  var CFG = window.DIAMOND_FEED_CONFIG || {};
  function shapeIcon(id) {
    return (CFG.shapeIcons && CFG.shapeIcons[id]) || (CFG.shapeIcons && CFG.shapeIcons.round) || '';
  }
  // Live stones may carry shapes outside the curated SHAPES list; prefer the
  // proxy-supplied display name, then the curated map, then a title-cased id.
  function shapeLabel(s) {
    return (s && s.shapeName) || (SHAPE_NAME && SHAPE_NAME[s.shape]) || titleCase(s.shape) || 'Diamond';
  }
  // Only round diamonds receive an official GIA cut grade; fancy shapes have none,
  // so show "N/A" rather than a misleading raw value.
  function cutLabel(s) {
    return (s && s.shape === 'round') ? ((s.cut == null || s.cut === '') ? '—' : s.cut) : 'N/A';
  }
  // loupe360 auto-rotates only when the path segment after /video/<w>/<h> is
  // "autoplay" (or "true"); Nivoda's URL omits it, so the spin otherwise loads as
  // a static first frame. Append it (preserving any trailing /rect|/app_only) when
  // autoplay is wanted; leave non-loupe360 URLs untouched.
  function spinAutoplay(url) {
    if (!url || !/loupe360\.com\//.test(url)) return url;
    var m = url.match(/^(.*\/video\/\d{2,4}\/\d{2,4})(\/.*)?$/);
    if (!m) return url;
    var rest = m[2] || '';
    if (/^\/(true|autoplay)(\/|$)/.test(rest)) return url;   // already set
    return m[1] + '/autoplay' + rest;
  }

  // Media for a stone's square frame. mode 'video' → 360° spin iframe (Nivoda's
  // `video` viewer) when available; otherwise the still image; otherwise the
  // shape glyph. opts.autoplay makes a loupe360 spin auto-rotate on load.
  // Returns absolutely-positioned markup that fills its container.
  function mediaHTML(s, mode, opts) {
    var m = (s && s.media) || {};
    if (mode === 'video' && m.video) {
      var src = (opts && opts.autoplay) ? spinAutoplay(m.video) : m.video;
      return '<iframe class="df-media__360" src="' + src + '" loading="lazy" ' +
        'allow="autoplay; fullscreen" referrerpolicy="no-referrer" title="360° view" scrolling="no"></iframe>';
    }
    // Glyph sits behind the image as a graceful fallback if the URL 404s.
    var glyph = '<span class="df-media__glyph" style="--shape-icon:url(' + shapeIcon(s.shape) + ')"></span>';
    if (m.image) {
      return glyph + '<img class="df-media__img" src="' + m.image + '" loading="lazy" alt="" ' +
        'onerror="this.style.display=\'none\'" />';
    }
    return glyph;
  }

  // Nivoda's 360° viewers (loupe360 …/video/500/500 etc.) render at the fixed pixel
  // size baked into their URL and do NOT scale to the iframe box — a smaller box just
  // clips them to the top-left corner (visible in the table's ~200px expanded media,
  // fine in the ~480px quick-view). Size each iframe to its native px and CSS-scale it
  // to fill whatever square container it sits in, re-fitting on container resize.
  function fit360(scope) {
    var frames = (scope || document).querySelectorAll('.df-media__360');
    Array.prototype.forEach.call(frames, function (f) {
      var box = f.parentElement;
      if (!box) return;
      var m = (f.getAttribute('src') || '').match(/\/(\d{2,4})\/(\d{2,4})(?:[/?#]|$)/);
      var nat = (m && parseInt(m[1], 10)) || 500;
      function apply() {
        var w = box.clientWidth || box.offsetWidth;
        if (!w) return;
        f.style.width = nat + 'px';
        f.style.height = nat + 'px';
        f.style.transformOrigin = 'top left';
        f.style.transform = 'scale(' + (w / nat) + ')';
      }
      apply();
      if (window.ResizeObserver && !f.__df_ro) {
        f.__df_ro = new ResizeObserver(apply);
        f.__df_ro.observe(box);
      }
    });
  }

  /* ---- Ordinal scales (low → high) ------------------------------------ */
  const COLOR   = ['M', 'L', 'K', 'J', 'I', 'H', 'G', 'F', 'E', 'D'];
  const CLARITY = ['SI2', 'SI1', 'VS2', 'VS1', 'VVS2', 'VVS1', 'IF', 'FL'];
  const CUT     = ['Good', 'Very Good', 'Excellent', 'Ideal'];
  const SHAPES  = [
    { id: 'round',    name: 'Round' },
    { id: 'oval',     name: 'Oval' },
    { id: 'pear',     name: 'Pear' },
    { id: 'emerald',  name: 'Emerald' },
    { id: 'cushion',  name: 'Cushion' },
    { id: 'radiant',  name: 'Radiant' },
    { id: 'princess', name: 'Princess' },
    { id: 'marquise', name: 'Marquise' },
    { id: 'asscher',  name: 'Asscher' },
    { id: 'hexagon',  name: 'Hexagon' }
  ];
  const SHAPE_NAME = Object.fromEntries(SHAPES.map(s => [s.id, s.name]));
  const FLUOR = ['None', 'Faint', 'Medium'];
  const GRADE = ['Good', 'Very Good', 'Excellent'];   // polish & symmetry scale (low → high)
  const CERTS = ['GIA', 'IGI'];

  /* ---- Quality code ↔ design label (cut/polish/symmetry) -------------- */
  var QUALITY_LABEL = { ID: 'Excellent', EIGHTX: 'Excellent', EX: 'Excellent', VG: 'Very Good', GD: 'Good', G: 'Good' };

  /* ---- Catalog rules by origin (driven by merchant floor settings) ---- */
  function ruleFromFloor(f) {
    f = f || {};
    return {
      colorMinIdx:   f.color   ? Math.max(0, COLOR.indexOf(f.color))     : 0,
      clarityMinIdx: f.clarity ? Math.max(0, CLARITY.indexOf(f.clarity)) : 0,
      // Polish/symmetry grades ≥ the floor (use the strictest of the three).
      grades: GRADE.slice(Math.max(0, GRADE.indexOf(
        QUALITY_LABEL[f.polish] || QUALITY_LABEL[f.symmetry] || QUALITY_LABEL[f.cut] || 'Good'))),
      hasFluor: (f.fluor || 'NON') !== 'NON'
    };
  }
  var ORIGIN_RULES = {
    lab:     ruleFromFloor((CFG.floor || {}).lab),
    natural: ruleFromFloor((CFG.floor || {}).natural)
  };

  /* ---- Formatters (null-safe; staging data has gaps) ------------------ */
  const fmtUSD   = n => (n == null ? '—' : '$' + Math.round(n).toLocaleString('en-US'));
  const fmtUSD0  = fmtUSD;
  const fmtCt    = c => (c == null ? '—' : Number(c).toFixed(2));
  const titleCase = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  /* ---- Range defaults (seed the sliders; overridable via config) ------ */
  var FLOOR = CFG.floor || {};
  var SMAX = CFG.sliderMax || {};
  function pickMin(a, b, d) { a = (a == null ? d : a); b = (b == null ? d : b); return Math.min(a, b); }
  var DEF = Object.assign({
    caratMin: pickMin((FLOOR.lab || {}).carat, (FLOOR.natural || {}).carat, 0),
    caratMax: SMAX.carat != null ? SMAX.carat : 5.0,
    priceMin: pickMin((FLOOR.lab || {}).price, (FLOOR.natural || {}).price, 0),
    priceMax: SMAX.price != null ? SMAX.price : 50000,
    tableMin: FLOOR.tableMin != null ? FLOOR.tableMin : 50,
    tableMax: FLOOR.tableMax != null ? FLOOR.tableMax : 75,
    ratioMin: 1.0, ratioMax: 2.5
  }, CFG.defaults || {});

  /* ---- Filter state --------------------------------------------------- */
  function defaultState() {
    return {
      type: 'all',                       // all | lab | natural
      shapes: new Set(),                 // empty = all
      caratMin: DEF.caratMin, caratMax: DEF.caratMax,
      priceMin: DEF.priceMin, priceMax: DEF.priceMax,
      colors: new Set(),                 // empty = all (multi-select grades)
      clarities: new Set(),
      polish: new Set(),
      symmetry: new Set(),
      fluor: new Set(),
      tableMin: DEF.tableMin, tableMax: DEF.tableMax,
      ratioMin: DEF.ratioMin, ratioMax: DEF.ratioMax,
      certs: new Set(),                  // empty = all (GIA / IGI)
      sort: 'featured'
    };
  }

  /* ---- URL params → initial filter state (menu deep links + flows) ----- */
  // ?shape=oval (singular, the designed deep link) plus shapes/type/view/sort
  // and range params for admin-built menu URLs. Returns { view } for the
  // controller (view itself lives outside filter state).
  function applyURLParams(state) {
    var out = { view: null, applied: false };
    var p;
    try { p = new URLSearchParams(window.location.search); } catch (e) { return out; }
    var validShape = function (id) { return SHAPES.some(function (x) { return x.id === id; }); };
    var one = p.get('shape');
    if (one && validShape(one)) { state.shapes.add(one); out.applied = true; }
    (p.get('shapes') || '').split(',').forEach(function (id) {
      id = id.trim();
      if (id && validShape(id)) { state.shapes.add(id); out.applied = true; }
    });
    var type = p.get('type');
    if (type === 'lab' || type === 'natural') { state.type = type; out.applied = true; }
    var num = function (k) { var v = parseFloat(p.get(k)); return isFinite(v) ? v : null; };
    [['caratMin', 'caratMin'], ['caratMax', 'caratMax'], ['priceMin', 'priceMin'], ['priceMax', 'priceMax']]
      .forEach(function (pair) {
        var v = num(pair[0]);
        if (v != null) { state[pair[1]] = v; out.applied = true; }
      });
    var csvSet = function (param, key, allowed) {
      (p.get(param) || '').split(',').forEach(function (v) {
        v = v.trim();
        if (v && allowed.indexOf(v) !== -1) { state[key].add(v); out.applied = true; }
      });
    };
    csvSet('colors', 'colors', COLOR);
    csvSet('clarities', 'clarities', CLARITY);
    csvSet('certs', 'certs', CERTS);
    var sort = p.get('sort');
    if (sort && /^(featured|price|carat|color|clarity|cut)(-(asc|desc))?$/.test(sort)) { state.sort = sort; }
    var view = p.get('view');
    if (view === 'grid' || view === 'table') out.view = view;
    // Collection path tag (/collections/diamonds/oval-cut, natural-diamonds, …),
    // injected by the section from the URL segment.
    var pathTag = CFG.pathTag;
    if (pathTag) {
      var m = /^(.+)-cut$/.exec(pathTag);
      if (m && validShape(m[1])) { state.shapes.add(m[1]); out.applied = true; }
      else if (pathTag === 'natural-diamonds') { state.type = 'natural'; out.applied = true; }
      else if (pathTag === 'lab-grown-diamonds' || pathTag === 'lab-diamonds') { state.type = 'lab'; out.applied = true; }
    }
    return out;
  }

  /* ---- Build-your-ring CTA label (flow-aware, per design) -------------- */
  // The grid controller caches ShannonRingFlow.flowStatus() on window.__shannonFlow.
  function addCTALabel() {
    var flow = window.__shannonFlow || {};
    return flow.awaitingDiamond
      ? 'Select This Stone — Complete Your Ring'
      : 'Select a Setting for This Stone';
  }

  /* ---- Fetch layer: filter state → proxy query ------------------------ */
  // Nivoda fluorescence + grade codes the proxy expects back from us.
  var FLUOR_CODE = { None: 'NON', Faint: 'FNT', Medium: 'MED', Strong: 'STG', 'Very Strong': 'VST' };

  function serializeState(state, page, limit, cursor) {
    var d = defaultState();
    var p = new URLSearchParams();
    if (state.type !== 'all') p.set('type', state.type);
    if (state.shapes.size) p.set('shapes', Array.from(state.shapes).join(','));
    if (state.caratMin !== d.caratMin || state.caratMax !== d.caratMax) {
      p.set('caratMin', state.caratMin); p.set('caratMax', state.caratMax);
    }
    if (state.priceMin !== d.priceMin || state.priceMax !== d.priceMax) {
      p.set('priceMin', state.priceMin); p.set('priceMax', state.priceMax);
    }
    if (state.colors.size) p.set('colors', Array.from(state.colors).join(','));
    if (state.clarities.size) p.set('clarities', Array.from(state.clarities).join(','));
    if (state.polish.size) p.set('polish', Array.from(state.polish).join(','));
    if (state.symmetry.size) p.set('symmetry', Array.from(state.symmetry).join(','));
    if (state.fluor.size) {
      p.set('fluor', Array.from(state.fluor).map(function (f) { return FLUOR_CODE[f] || f; }).join(','));
    }
    if (state.tableMin !== d.tableMin || state.tableMax !== d.tableMax) {
      p.set('tableMin', state.tableMin); p.set('tableMax', state.tableMax);
    }
    if (state.ratioMin !== d.ratioMin || state.ratioMax !== d.ratioMax) {
      p.set('ratioMin', state.ratioMin); p.set('ratioMax', state.ratioMax);
    }
    if (state.certs.size) p.set('certs', Array.from(state.certs).join(','));
    if (state.sort && state.sort !== 'featured') p.set('sort', state.sort);
    if (CFG.floor) p.set('floor', JSON.stringify(CFG.floor));   // authoritative house catalog floor
    p.set('page', page || 0);
    p.set('limit', limit || 24);
    // Proxy-issued pagination cursor (per-origin offsets for the "all" view, where
    // the two origin streams advance unevenly — page*limit alone would skip stones).
    if (cursor) p.set('cursor', JSON.stringify(cursor));
    return p.toString();
  }

  // Requests time out rather than hang: a stalled connection (seen on some
  // phone networks) used to leave the controller waiting forever, so the page
  // sat on "Finding diamonds…" with nothing to retry. A timed-out request
  // rejects with a plain Error (not AbortError) so callers treat it as a
  // failure; an AbortError still means "superseded by a newer request".
  var FEED_TIMEOUT_MS = 25000;
  var COUNT_TIMEOUT_MS = 30000;
  function timedFetch(url, slot, timeoutMs) {
    if (slot.ctrl) slot.ctrl.abort();
    var ctrl = new AbortController();
    slot.ctrl = ctrl;
    var timedOut = false;
    var timer = setTimeout(function () { timedOut = true; ctrl.abort(); }, timeoutMs);
    return fetch(url, { signal: ctrl.signal })
      .then(function (r) {
        if (!r.ok) throw new Error(slot.label + ' HTTP ' + r.status);
        return r.json();
      })
      .then(function (j) {
        clearTimeout(timer);
        if (slot.ctrl === ctrl) slot.ctrl = null;
        return j;
      })
      .catch(function (err) {
        clearTimeout(timer);
        if (err && err.name === 'AbortError' && !timedOut) throw err;   // superseded
        if (slot.ctrl === ctrl) slot.ctrl = null;
        if (timedOut) { var e = new Error(slot.label + ' timed out'); e.timedOut = true; throw e; }
        throw err;
      });
  }

  var _feedSlot = { ctrl: null, label: 'Feed' };
  function fetchFeed(state, page, limit, cursor) {
    if (!CFG.proxyUrl) return Promise.reject(new Error('DIAMOND_FEED_CONFIG.proxyUrl not set'));
    var url = CFG.proxyUrl.replace(/\/$/, '') + '/feed?' + serializeState(state, page, limit, cursor);
    return timedFetch(url, _feedSlot, FEED_TIMEOUT_MS);
  }

  // The total count is slower than the items, so it's a separate request the
  // controller fires in parallel and fills in when it resolves.
  var _countSlot = { ctrl: null, label: 'Count' };
  function fetchCount(state) {
    if (!CFG.proxyUrl) return Promise.reject(new Error('DIAMOND_FEED_CONFIG.proxyUrl not set'));
    var url = CFG.proxyUrl.replace(/\/$/, '') + '/count?' + serializeState(state, 0, 1);
    return timedFetch(url, _countSlot, COUNT_TIMEOUT_MS);
  }

  function countActive(state) {
    const d = defaultState();
    let n = 0;
    if (state.type !== 'all') n++;
    if (state.shapes.size) n++;
    if (state.caratMin !== d.caratMin || state.caratMax !== d.caratMax) n++;
    if (state.priceMin !== d.priceMin || state.priceMax !== d.priceMax) n++;
    if (state.colors.size) n++;
    if (state.clarities.size) n++;
    if (state.polish.size) n++;
    if (state.symmetry.size) n++;
    if (state.fluor.size) n++;
    if (state.tableMin !== d.tableMin || state.tableMax !== d.tableMax) n++;
    if (state.ratioMin !== d.ratioMin || state.ratioMax !== d.ratioMax) n++;
    if (state.certs.size) n++;
    return n;
  }

  // Active-filter descriptions, shared by both views' chip rows.
  // Returns [key, label, clear()] tuples.
  function describeChips(state) {
    const d = defaultState();
    const chips = [];
    if (state.type !== 'all') chips.push(['origin', state.type === 'lab' ? 'Lab-Grown' : 'Natural', () => { state.type = 'all'; }]);
    state.shapes.forEach(sh => chips.push(['shape-' + sh, SHAPE_NAME[sh], () => state.shapes.delete(sh)]));
    if (state.caratMin !== d.caratMin || state.caratMax !== d.caratMax)
      chips.push(['carat', fmtCt(state.caratMin) + '–' + fmtCt(state.caratMax) + ' ct', () => { state.caratMin = d.caratMin; state.caratMax = d.caratMax; }]);
    if (state.priceMin !== d.priceMin || state.priceMax !== d.priceMax)
      chips.push(['price', fmtUSD(state.priceMin) + '–' + fmtUSD(state.priceMax), () => { state.priceMin = d.priceMin; state.priceMax = d.priceMax; }]);
    state.colors.forEach(c => chips.push(['color-' + c, 'Color ' + c, () => state.colors.delete(c)]));
    state.clarities.forEach(c => chips.push(['clarity-' + c, c, () => state.clarities.delete(c)]));
    state.polish.forEach(p => chips.push(['polish-' + p, 'Polish ' + p, () => state.polish.delete(p)]));
    state.symmetry.forEach(p => chips.push(['symm-' + p, 'Symmetry ' + p, () => state.symmetry.delete(p)]));
    state.fluor.forEach(f => chips.push(['fluor-' + f, 'Fluor. ' + f, () => state.fluor.delete(f)]));
    if (state.tableMin !== d.tableMin || state.tableMax !== d.tableMax)
      chips.push(['table', 'Table ' + state.tableMin + '–' + state.tableMax + '%', () => { state.tableMin = d.tableMin; state.tableMax = d.tableMax; }]);
    if (state.ratioMin !== d.ratioMin || state.ratioMax !== d.ratioMax)
      chips.push(['ratio', 'Ratio ' + state.ratioMin.toFixed(2) + '–' + state.ratioMax.toFixed(2), () => { state.ratioMin = d.ratioMin; state.ratioMax = d.ratioMax; }]);
    state.certs.forEach(l => chips.push(['cert-' + l, l, () => state.certs.delete(l)]));
    return chips;
  }

  /* =====================================================================
     DUAL-HANDLE RANGE SLIDER
     mode 'num' → continuous (carat, price); 'ord' → stepped labels.
  ===================================================================== */
  function makeRange(opts) {
    // opts: { mode, min, max, step, values, lo, hi, format, onInput, onCommit,
    //         editable, prefix, unit, decimals }
    const isOrd = opts.mode === 'ord';
    const min = isOrd ? 0 : opts.min;
    const max = isOrd ? opts.values.length - 1 : opts.max;
    const step = isOrd ? 1 : (opts.step || 1);
    const editable = !!opts.editable;
    const decimals = opts.decimals != null ? opts.decimals : 0;
    const fmtIn = v => (decimals > 0 ? Number(v).toFixed(decimals) : String(Math.round(v)));

    const root = document.createElement('div');
    root.className = 'rng';
    const headInner = editable
      ? '<span class="rng__field">' + (opts.prefix ? '<span class="rng__pfx">' + opts.prefix + '</span>' : '') +
          '<input class="rng__in rng__lo-in" type="number" inputmode="decimal" aria-label="Minimum">' +
          (opts.unit ? '<span class="rng__unit">' + opts.unit + '</span>' : '') + '</span>' +
        '<span class="rng__dash">–</span>' +
        '<span class="rng__field">' + (opts.prefix ? '<span class="rng__pfx">' + opts.prefix + '</span>' : '') +
          '<input class="rng__in rng__hi-in" type="number" inputmode="decimal" aria-label="Maximum">' +
          (opts.unit ? '<span class="rng__unit">' + opts.unit + '</span>' : '') + '</span>'
      : '<span class="rng__lo"></span><span class="rng__dash">–</span><span class="rng__hi"></span>';
    root.innerHTML =
      '<div class="rng__head' + (editable ? ' rng__head--edit' : '') + '">' + headInner + '</div>' +
      '<div class="rng__rail">' +
        '<div class="rng__fill"></div>' +
        '<input type="range" class="rng__a" aria-label="Minimum">' +
        '<input type="range" class="rng__b" aria-label="Maximum">' +
      '</div>';

    const a = root.querySelector('.rng__a');
    const b = root.querySelector('.rng__b');
    const fill = root.querySelector('.rng__fill');
    const loEl = root.querySelector('.rng__lo');
    const hiEl = root.querySelector('.rng__hi');
    const loIn = root.querySelector('.rng__lo-in');
    const hiIn = root.querySelector('.rng__hi-in');
    [a, b].forEach(inp => { inp.min = min; inp.max = max; inp.step = step; });
    if (editable) { [loIn, hiIn].forEach(inp => { inp.min = min; inp.max = max; inp.step = step; }); }

    let lo = opts.lo, hi = opts.hi;
    a.value = lo; b.value = hi;

    const fmt = isOrd ? (v => opts.values[v]) : opts.format;

    function paint() {
      const pa = (lo - min) / (max - min) * 100;
      const pb = (hi - min) / (max - min) * 100;
      fill.style.left  = pa + '%';
      fill.style.right = (100 - pb) + '%';
      if (editable) {
        if (document.activeElement !== loIn) loIn.value = fmtIn(lo);
        if (document.activeElement !== hiIn) hiIn.value = fmtIn(hi);
      } else {
        loEl.textContent = fmt(lo);
        hiEl.textContent = fmt(hi);
      }
    }
    function clamp() {
      lo = +a.value; hi = +b.value;
      if (lo > hi) { // keep handles from crossing
        if (document.activeElement === a) { hi = lo; b.value = hi; }
        else { lo = hi; a.value = lo; }
      }
      // raise whichever handle is grabbed so it stays on top near the ends
      a.style.zIndex = (lo > max - (max - min) * 0.12) ? 5 : 3;
      paint();
    }
    function handle(commit) {
      clamp();
      opts.onInput && opts.onInput(lo, hi);
      if (commit) opts.onCommit && opts.onCommit(lo, hi);
    }
    a.addEventListener('input', () => handle(false));
    b.addEventListener('input', () => handle(false));
    a.addEventListener('change', () => handle(true));
    b.addEventListener('change', () => handle(true));

    if (editable) {
      const commitIn = () => {
        let nlo = parseFloat(loIn.value);
        let nhi = parseFloat(hiIn.value);
        if (isNaN(nlo)) nlo = min;
        if (isNaN(nhi)) nhi = max;
        nlo = Math.min(Math.max(nlo, min), max);
        nhi = Math.min(Math.max(nhi, min), max);
        if (nlo > nhi) { const t = nlo; nlo = nhi; nhi = t; }
        lo = nlo; hi = nhi; a.value = lo; b.value = hi;
        paint();
        opts.onInput && opts.onInput(lo, hi);
        opts.onCommit && opts.onCommit(lo, hi);
      };
      [loIn, hiIn].forEach(inp => {
        inp.addEventListener('change', commitIn);
        inp.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); inp.blur(); } });
      });
    }

    paint();

    return {
      el: root,
      get lo() { return lo; },
      get hi() { return hi; },
      set(nlo, nhi) { lo = a.value = nlo; hi = b.value = nhi; clamp(); }
    };
  }

  /* =====================================================================
     QUICK-VIEW DRAWER (built once, shared by both pages)
  ===================================================================== */
  let qv = null;
  function ensureQuickView() {
    if (qv) return qv;
    const wrap = document.createElement('div');
    wrap.className = 'dqv';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.innerHTML =
      '<div class="dqv__scrim" data-qv-close></div>' +
      '<aside class="dqv__panel" role="dialog" aria-modal="true" aria-label="Diamond details">' +
        '<button class="dqv__close" data-qv-close aria-label="Close">&times;</button>' +
        '<div class="dqv__media">' +
          '<video-slot id="dqv-360" class="dqv__360" placeholder="360° rotation · drop stone video"></video-slot>' +
          '<span class="dqv__shape" aria-hidden="true"></span>' +
          '<span class="dqv__origin"></span>' +
        '</div>' +
        '<div class="dqv__scroll">' +
          '<p class="dqv__eyebrow"></p>' +
          '<h2 class="dqv__title serif"></h2>' +
          '<div class="dqv__cs"></div>' +
          '<dl class="dqv__specs"></dl>' +
        '</div>' +
        '<div class="dqv__foot">' +
          '<div class="dqv__price"><span class="dqv__price-k">Total</span><span class="dqv__price-v"></span></div>' +
          '<button class="dqv__add" type="button">Add this stone to a ring</button>' +
        '</div>' +
      '</aside>';
    document.body.appendChild(wrap);

    const panel = wrap.querySelector('.dqv__panel');
    const close = () => { wrap.classList.remove('is-open'); wrap.setAttribute('aria-hidden', 'true'); };
    wrap.addEventListener('click', e => { if (e.target.closest('[data-qv-close]')) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

    const els = {
      wrap, panel,
      media:   wrap.querySelector('.dqv__media'),
      eyebrow: wrap.querySelector('.dqv__eyebrow'),
      title:   wrap.querySelector('.dqv__title'),
      cs:      wrap.querySelector('.dqv__cs'),
      specs:   wrap.querySelector('.dqv__specs'),
      shape:   wrap.querySelector('.dqv__shape'),
      origin:  wrap.querySelector('.dqv__origin'),
      priceV:  wrap.querySelector('.dqv__price-v'),
      add:     wrap.querySelector('.dqv__add')
    };
    const dash = v => (v == null || v === '' ? '—' : v);

    qv = {
      open(s) {
        const name = shapeLabel(s);
        els.eyebrow.textContent = (s.type === 'lab' ? 'Lab-Grown' : 'Natural') + ' Diamond';
        els.title.textContent = fmtCt(s.carat) + ' ct ' + name;
        // Detail view: show the 360° spin (or image) via the shared helper.
        // Auto-rotate on mobile (loupe360 needs the autoplay flag).
        var qvAutoplay = !!(window.matchMedia && window.matchMedia('(max-width: 680px)').matches);
        var oldMedia = els.media.querySelector('.df-media__img, .df-media__360, .df-media__glyph');
        if (oldMedia) oldMedia.remove();
        els.media.insertAdjacentHTML('afterbegin', mediaHTML(s, 'video', { autoplay: qvAutoplay }));
        fit360(els.media);
        els.shape.style.display = 'none';
        els.origin.textContent = s.type === 'lab' ? 'Lab-Grown' : 'Natural';
        els.origin.className = 'dqv__origin is-' + s.type;
        els.cs.innerHTML = [
          ['Carat', fmtCt(s.carat)],
          ['Color', dash(s.color)],
          ['Clarity', dash(s.clarity)],
          ['Cut', cutLabel(s)]
        ].map(([k, v]) => '<div class="dqv__c"><span class="k">' + k + '</span><span class="v">' + v + '</span></div>').join('');
        // showCert=false → grading lab only: no number, and no report link
        // either (the PDF carries the number).
        const certCell = CFG.showCert === false
          ? dash(s.lab)
          : (s.lab ? s.lab + ' · ' : '') + (s.media && s.media.pdf
              ? '<a class="spec-link" href="' + s.media.pdf + '" target="_blank" rel="noopener">' + dash(s.certNumber) + '</a>'
              : dash(s.certNumber));
        els.specs.innerHTML = [
          ['Shape', name],
          ['L/W Ratio', s.ratio != null ? s.ratio.toFixed(2) : '—'],
          ['Measurements', s.meas ? s.meas + ' mm' : '—'],
          ['Table', s.table != null ? s.table + '%' : '—'],
          ['Depth', s.depth != null ? s.depth + '%' : '—'],
          ['Fluorescence', dash(s.fluor)],
          ['Polish', dash(s.polish)],
          ['Symmetry', dash(s.symmetry)],
          ['Certificate', certCell]
        ].map(([k, v]) => '<div class="dqv__row"><dt>' + k + '</dt><dd>' + v + '</dd></div>').join('');
        els.priceV.textContent = fmtUSD(s.price);
        els.add.textContent = addCTALabel();
        els.add.onclick = () => {
          if (typeof CFG.onAdd === 'function') {
            // Close only on success — failures keep the drawer open so the
            // button can show what went wrong ("Unavailable", "Try again").
            var p = CFG.onAdd(s, els.add);
            if (p && typeof p.then === 'function') {
              p.then(function (ok) { if (ok !== false) close(); }).catch(function () {});
            } else {
              close();
            }
          } else if (window.DiamondFeedCart) {
            window.DiamondFeedCart.add(s);
            close();
          }
        };
        wrap.classList.add('is-open');
        wrap.setAttribute('aria-hidden', 'false');
        els.panel.scrollTop = 0;
      },
      close
    };
    return qv;
  }

  /* ---- Shared CSS (slider + quick-view drawer) ------------------------ */
  const SHARED_CSS = `
  /* ----- Shared filter controls (origin seg, shape chips, groups) ----- */
  .dfg { padding: 22px 0; border-bottom: 1px solid var(--line); }
  .dfg:last-child { border-bottom: 0; }
  .dfg__label {
    font-size: 10.5px; letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--ink-faint); margin: 0 0 15px;
  }
  .seg {
    display: grid; grid-auto-flow: column; grid-auto-columns: 1fr;
    border: 1px solid var(--line-strong);
  }
  .seg button {
    background: none; border: 0; border-right: 1px solid var(--line);
    padding: 10px 6px; cursor: pointer; font: inherit;
    font-size: 11px; letter-spacing: 0.08em; color: var(--ink-soft);
    transition: background .15s ease, color .15s ease;
  }
  .seg button:last-child { border-right: 0; }
  .seg button:hover { color: var(--ink); }
  .seg button.is-on { background: var(--ink); color: #fff; }
  .dfg__shapes { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .dchip {
    display: flex; flex-direction: column; align-items: center; gap: 9px;
    border: 1px solid var(--line); background: none; cursor: pointer;
    padding: 14px 6px; font: inherit; font-size: 10.5px; letter-spacing: 0.06em;
    color: var(--ink-soft); transition: border-color .15s ease, color .15s ease, background .15s ease;
  }
  .dchip:hover { border-color: var(--line-strong); color: var(--ink); }
  .dchip.is-on { border-color: var(--ink); background: var(--bg-soft); color: var(--ink); }
  .dchip__icon {
    width: 28px; height: 28px; background-color: currentColor; opacity: 0.85;
    -webkit-mask: var(--shape-icon) center / contain no-repeat;
            mask: var(--shape-icon) center / contain no-repeat;
  }

  /* ----- Dual-handle range slider ----- */
  .rng { --rng-h: 30px; }
  .rng__head {
    display: flex; align-items: baseline; gap: 6px;
    font-family: 'Cormorant Garamond', serif; font-style: italic;
    font-size: 17px; color: var(--ink); margin-bottom: 10px;
  }
  .rng__dash { color: var(--ink-faint); font-style: normal; font-size: 13px; }
  .rng__rail { position: relative; height: var(--rng-h); }
  .rng__rail::before {
    content: ""; position: absolute; left: 0; right: 0; top: 50%;
    height: 2px; transform: translateY(-50%);
    background: var(--line); border-radius: 2px;
  }
  .rng__fill {
    position: absolute; top: 50%; height: 2px; transform: translateY(-50%);
    background: var(--ink); border-radius: 2px;
  }
  .rng input[type=range] {
    position: absolute; left: 0; top: 0; width: 100%; height: var(--rng-h);
    margin: 0; background: none; pointer-events: none;
    -webkit-appearance: none; appearance: none;
  }
  .rng input[type=range]:focus { outline: none; }
  .rng input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none; appearance: none;
    width: 18px; height: 18px; border-radius: 50%;
    background: var(--bg); border: 1.5px solid var(--ink);
    box-shadow: 0 1px 4px rgba(42,33,32,0.22);
    cursor: grab; pointer-events: auto; margin-top: 0;
    transition: transform .12s ease;
  }
  .rng input[type=range]::-webkit-slider-thumb:hover { transform: scale(1.12); }
  .rng input[type=range]:active::-webkit-slider-thumb { cursor: grabbing; }
  .rng input[type=range]::-moz-range-thumb {
    width: 18px; height: 18px; border-radius: 50%;
    background: var(--bg); border: 1.5px solid var(--ink);
    box-shadow: 0 1px 4px rgba(42,33,32,0.22);
    cursor: grab; pointer-events: auto;
  }
  .rng input[type=range]::-webkit-slider-runnable-track { background: none; }
  .rng input[type=range]::-moz-range-track { background: none; }

  /* ----- Editable min/max fields on a range ----- */
  .rng__head--edit { gap: 9px; margin-bottom: 13px; }
  .rng__field {
    display: inline-flex; align-items: baseline; gap: 3px; flex: 1 1 0; min-width: 0;
    border: 1px solid var(--line-strong); padding: 7px 10px; background: var(--bg);
    transition: border-color .15s ease;
  }
  .rng__field:focus-within { border-color: var(--ink); }
  .rng__pfx, .rng__unit {
    font-family: 'Jost', sans-serif; font-style: normal; font-size: 11px;
    color: var(--ink-faint); letter-spacing: 0.04em; flex: none;
  }
  .rng__in {
    width: 100%; min-width: 0; border: 0; background: none; padding: 0;
    font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 17px; color: var(--ink);
    -moz-appearance: textfield;
  }
  .rng__in:focus { outline: none; }
  .rng__in::-webkit-outer-spin-button, .rng__in::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

  /* ----- Multi-select grade boxes (colour, clarity, polish, etc.) ----- */
  .dboxes { display: flex; flex-wrap: wrap; gap: 7px; }
  .dboxes--row { flex-wrap: nowrap; gap: 5px; }
  .dboxes--row .dbox { flex: 1 1 0; min-width: 0; padding-left: 4px; padding-right: 4px; }
  .dbox {
    border: 1px solid var(--line-strong); background: none; cursor: pointer; font: inherit;
    font-size: 11.5px; letter-spacing: 0.04em; color: var(--ink-soft);
    padding: 9px 13px; min-width: 44px; text-align: center;
    transition: background .15s ease, color .15s ease, border-color .15s ease;
  }
  .dbox:hover { border-color: var(--ink); color: var(--ink); }
  .dbox.is-on { background: var(--ink); border-color: var(--ink); color: #fff; }
  .dbox[hidden] { display: none; }

  /* ----- Advanced (collapsible) section ----- */
  .dadv { border-bottom: 1px solid var(--line); }
  .dadv:last-child { border-bottom: 0; }
  .dadv__toggle {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    background: none; border: 0; cursor: pointer; font: inherit; padding: 20px 0;
    font-size: 10.5px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--ink);
  }
  .dadv__chev {
    width: 0; height: 0; border-left: 4px solid transparent; border-right: 4px solid transparent;
    border-top: 5px solid var(--ink-soft); transition: transform .2s ease;
  }
  .dadv.is-open .dadv__chev { transform: rotate(180deg); }
  .dadv__body { display: none; padding-bottom: 4px; }
  .dadv.is-open .dadv__body { display: block; }
  .dadv__body .dfg:first-child { padding-top: 4px; }
  .dadv__body .dfg:last-child { border-bottom: 0; }

  /* ----- Informational note (e.g. Cut) ----- */
  .dfg__note { margin: -4px 0 0; font-size: 12.5px; line-height: 1.6; color: var(--ink-soft); text-wrap: pretty; }

  /* ----- Quick-view drawer ----- */
  .dqv { position: fixed; inset: 0; z-index: 120; pointer-events: none; }
  /* display:block (2-class selector) defeats base.css's \`div:empty { display:none }\`,
     which otherwise removes this empty div — killing the dim layer AND click-outside-to-close. */
  .dqv .dqv__scrim {
    display: block; position: absolute; inset: 0; background: rgba(30,22,20,0.40);
    opacity: 0; transition: opacity .35s ease;
  }
  .dqv__panel {
    position: absolute; top: 0; right: 0; bottom: 0;
    width: min(480px, 92vw); background: var(--bg);
    border-left: 1px solid var(--line);
    box-shadow: -8px 0 48px rgba(30,22,20,0.18);
    transform: translateX(100%);
    transition: transform .4s cubic-bezier(.22,.61,.36,1);
    display: flex; flex-direction: column; overflow: hidden;
  }
  .dqv.is-open { pointer-events: auto; }
  .dqv.is-open .dqv__scrim { opacity: 1; }
  .dqv.is-open .dqv__panel { transform: translateX(0); }
  .dqv__close {
    position: absolute; top: 14px; right: 14px; z-index: 4;
    width: 38px; height: 38px; border: 0; cursor: pointer;
    background: rgba(255,255,255,0.85); color: var(--ink);
    font-size: 24px; line-height: 1; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    transition: background .15s ease;
  }
  .dqv__close:hover { background: #fff; }
  .dqv__media {
    position: relative; flex: none; aspect-ratio: 1 / 1;
    background: var(--bg-soft); overflow: hidden;
  }
  .dqv__media .dqv__360 {
    --video-slot-bg: var(--bg-soft); --video-slot-fg: var(--ink-faint);
    width: 100%; height: 100%; display: block;
  }
  .dqv__shape {
    position: absolute; left: 50%; top: 50%;
    width: 116px; height: 116px; transform: translate(-50%, -50%);
    background-color: var(--ink); opacity: 0.12;
    -webkit-mask: var(--shape-icon) center / contain no-repeat;
            mask: var(--shape-icon) center / contain no-repeat;
    pointer-events: none;
  }
  .dqv__origin {
    position: absolute; left: 16px; top: 16px;
    font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
    padding: 6px 11px; background: rgba(255,255,255,0.94); color: var(--ink);
    border: 1px solid var(--line);
  }
  .dqv__origin.is-lab { color: var(--accent); }
  .dqv__scroll { flex: 1 1 auto; overflow-y: auto; padding: 26px var(--pad-x, 28px) 20px; }
  .dqv__eyebrow {
    font-size: 10.5px; letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--ink-faint); margin: 0 0 8px;
  }
  .dqv__title { font-size: clamp(28px, 3.6vw, 38px); line-height: 1.04; margin: 0 0 22px; font-weight: 300; }
  .dqv__cs {
    display: grid; grid-template-columns: repeat(4, 1fr);
    border: 1px solid var(--line); margin-bottom: 22px;
  }
  .dqv__c { padding: 14px 6px; text-align: center; border-right: 1px solid var(--line); }
  .dqv__c:last-child { border-right: 0; }
  .dqv__c .k { display: block; font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--ink-faint); margin-bottom: 6px; }
  .dqv__c .v { display: block; font-family: 'Cormorant Garamond', serif; font-size: 24px; color: var(--ink); line-height: 1; }
  .dqv__specs { margin: 0 0 22px; }
  .dqv__row { display: flex; justify-content: space-between; gap: 16px; padding: 11px 0; border-bottom: 1px solid var(--line); }
  .dqv__row:last-child { border-bottom: 0; }
  .dqv__row dt { color: var(--ink-soft); font-size: 12.5px; }
  .dqv__row dd { margin: 0; color: var(--ink); font-size: 12.5px; text-align: right; }
  .spec-link { color: var(--ink); text-decoration: none; border-bottom: 1px solid var(--line-strong); padding-bottom: 1px; transition: color .15s ease, border-color .15s ease; }
  .spec-link:hover { color: var(--accent); border-color: var(--accent); }
  .dqv__cert {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    padding: 16px; background: var(--bg-soft); font-size: 12px;
  }
  .dqv__cert-lab { font-weight: 500; letter-spacing: 0.04em; }
  .dqv__cert-no { color: var(--ink-soft); }
  .dqv__cert-link { margin-left: auto; color: var(--ink); text-decoration: none; border-bottom: 1px solid var(--ink); padding-bottom: 2px; font-size: 11px; letter-spacing: 0.04em; }
  .dqv__cert-link:hover { color: var(--accent); border-color: var(--accent); }
  .dqv__foot {
    flex: none; display: flex; align-items: center; gap: 16px;
    padding: 16px var(--pad-x, 28px); border-top: 1px solid var(--line); background: var(--bg);
  }
  .dqv__price { display: flex; flex-direction: column; gap: 2px; }
  .dqv__price-k { font-size: 9.5px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-faint); }
  .dqv__price-v { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 26px; color: var(--ink); line-height: 1; }
  .dqv__add {
    flex: 1 1 auto; background: var(--ink); color: #fff; border: 1px solid var(--ink);
    cursor: pointer; font: inherit; font-size: 11.5px; letter-spacing: 0.16em;
    text-transform: uppercase; padding: 15px 18px; transition: background .2s ease;
  }
  .dqv__add:hover { background: #000; }
  @media (prefers-reduced-motion: reduce) {
    .dqv__panel, .dqv__scrim { transition: none; }
  }
  `;

  /* =====================================================================
     BUILD FILTER CONTROLS into a container (shared by both pages).
     Returns { sync } to push state → controls (used by Clear all).
  ===================================================================== */
  function buildFilters(container, state, onChange) {
    const fire = () => onChange && onChange(state);
    const CUT_NOTE = 'Round diamonds are the only shape with an official GIA cut grade, and SHANNON exclusively carries Excellent cut round diamonds. Fancy shapes like oval, cushion, and emerald do not receive an official cut grade from GIA, so no cut filter is needed.';

    container.innerHTML =
      '<div class="dfg dfg--origin">' +
        '<p class="dfg__label">Origin</p>' +
        '<div class="seg" data-origin>' +
          '<button type="button" data-val="all" class="is-on">All</button>' +
          '<button type="button" data-val="lab">Lab-Grown</button>' +
          '<button type="button" data-val="natural">Natural</button>' +
        '</div>' +
      '</div>' +
      '<div class="dfg">' +
        '<p class="dfg__label">Shape</p>' +
        '<div class="dfg__shapes" data-shapes></div>' +
      '</div>' +
      '<div class="dfg"><p class="dfg__label">Carat</p><div data-slot="carat"></div></div>' +
      '<div class="dfg"><p class="dfg__label">Price</p><div data-slot="price"></div></div>' +
      '<div class="dfg"><p class="dfg__label">Color</p><div class="dboxes dboxes--row" data-box="colors"></div></div>' +
      '<div class="dfg"><p class="dfg__label">Clarity</p><div class="dboxes" data-box="clarities"></div></div>' +
      '<div class="dfg"><p class="dfg__label">Cut</p><p class="dfg__note">' + CUT_NOTE + '</p></div>' +
      '<div class="dadv" data-adv>' +
        '<button type="button" class="dadv__toggle" aria-expanded="false"><span>Advanced</span><span class="dadv__chev" aria-hidden="true"></span></button>' +
        '<div class="dadv__body">' +
          '<div class="dfg" data-group="polish"><p class="dfg__label">Polish</p><div class="dboxes" data-box="polish"></div></div>' +
          '<div class="dfg" data-group="symmetry"><p class="dfg__label">Symmetry</p><div class="dboxes" data-box="symmetry"></div></div>' +
          '<div class="dfg" data-group="fluor"><p class="dfg__label">Fluorescence</p><div class="dboxes" data-box="fluor"></div></div>' +
          '<div class="dfg"><p class="dfg__label">Table</p><div data-slot="table"></div></div>' +
          '<div class="dfg"><p class="dfg__label">L/W Ratio</p><div data-slot="ratio"></div></div>' +
          '<div class="dfg"><p class="dfg__label">Certificate</p><div class="dboxes" data-box="certs"></div></div>' +
        '</div>' +
      '</div>';

    // Advanced section (collapsed by default)
    const adv = container.querySelector('[data-adv]');
    adv.querySelector('.dadv__toggle').addEventListener('click', () => {
      const open = adv.classList.toggle('is-open');
      adv.querySelector('.dadv__toggle').setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Origin segmented — also limits the grade options available below
    const seg = container.querySelector('[data-origin]');
    seg.addEventListener('click', e => {
      const btn = e.target.closest('button'); if (!btn) return;
      state.type = btn.dataset.val;
      seg.querySelectorAll('button').forEach(b => b.classList.toggle('is-on', b === btn));
      applyOriginConstraints();
      fire();
    });

    // Shapes — limited to the merchant's "shapes included" floor.
    const allowShapes = (CFG.floor && CFG.floor.shapes && CFG.floor.shapes.length) ? CFG.floor.shapes : null;
    const visibleShapes = allowShapes ? SHAPES.filter(s => allowShapes.indexOf(s.id) !== -1) : SHAPES;
    const shapesWrap = container.querySelector('[data-shapes]');
    shapesWrap.innerHTML = visibleShapes.map(s =>
      '<button type="button" class="dchip" data-shape="' + s.id + '">' +
        '<span class="dchip__icon" style="--shape-icon:url(' + shapeIcon(s.id) + ')"></span>' +
        '<span>' + s.name + '</span>' +
      '</button>').join('');
    shapesWrap.addEventListener('click', e => {
      const btn = e.target.closest('.dchip'); if (!btn) return;
      const id = btn.dataset.shape;
      if (state.shapes.has(id)) state.shapes.delete(id); else state.shapes.add(id);
      btn.classList.toggle('is-on', state.shapes.has(id));
      fire();
    });

    // Multi-select grade boxes (colour, clarity, polish, symmetry, fluorescence, certificate)
    const boxGroups = [];
    function buildBoxes(key, values) {
      const wrap = container.querySelector('[data-box="' + key + '"]');
      wrap.innerHTML = values.map(v =>
        '<button type="button" class="dbox" data-v="' + v + '">' + v + '</button>').join('');
      wrap.addEventListener('click', e => {
        const b = e.target.closest('.dbox'); if (!b) return;
        const v = b.dataset.v;
        if (state[key].has(v)) state[key].delete(v); else state[key].add(v);
        b.classList.toggle('is-on', state[key].has(v));
        fire();
      });
      boxGroups.push({ key, wrap });
    }
    buildBoxes('colors',    COLOR.slice().reverse());            // D → M
    buildBoxes('clarities', CLARITY.slice().reverse());          // FL → SI2
    buildBoxes('polish',    GRADE.slice().reverse());            // Excellent → Good
    buildBoxes('symmetry',  GRADE.slice().reverse());
    buildBoxes('fluor',     FLUOR.slice());                      // None, Faint, Medium
    var certValues = (CFG.floor && CFG.floor.labs && CFG.floor.labs.length) ? CFG.floor.labs : CERTS.slice();
    buildBoxes('certs',     certValues);                         // merchant's certificate labs

    // Origin-aware option limits: show only the grades SHANNON carries for the
    // chosen origin, and hide Polish / Symmetry / Fluorescence for lab-grown
    // (always Excellent, and no fluorescence).
    const fluorGroup = container.querySelector('[data-group="fluor"]');
    const polishGroup = container.querySelector('[data-group="polish"]');
    const symmetryGroup = container.querySelector('[data-group="symmetry"]');
    function rulesFor(type) {
      const lab = ORIGIN_RULES.lab, nat = ORIGIN_RULES.natural;
      // 'all' shows the widest catalog (natural's broader colour + every grade).
      const r = type === 'lab' ? lab : nat;
      return {
        colors:    new Set(COLOR.filter((c, i) => i >= r.colorMinIdx)),
        clarities: new Set(CLARITY.filter((c, i) => i >= r.clarityMinIdx)),
        grades:    new Set(r.grades)
      };
    }
    function limitBoxes(key, allow) {
      const g = boxGroups.find(x => x.key === key);
      g.wrap.querySelectorAll('.dbox').forEach(b => {
        const ok = allow.has(b.dataset.v);
        b.hidden = !ok;
        if (!ok && state[key].has(b.dataset.v)) { state[key].delete(b.dataset.v); b.classList.remove('is-on'); }
      });
    }
    function clearGroup(key) {
      state[key].clear();
      const g = boxGroups.find(x => x.key === key);
      g.wrap.querySelectorAll('.dbox').forEach(b => b.classList.remove('is-on'));
    }
    function applyOriginConstraints() {
      const r = rulesFor(state.type);
      const isLab = state.type === 'lab';
      limitBoxes('colors', r.colors);
      limitBoxes('clarities', r.clarities);
      // Lab-grown: polish & symmetry are always Excellent and there is no
      // fluorescence → hide all three groups entirely.
      polishGroup.hidden = isLab;
      symmetryGroup.hidden = isLab;
      fluorGroup.hidden = isLab;
      if (isLab) { clearGroup('polish'); clearGroup('symmetry'); clearGroup('fluor'); }
      else { limitBoxes('polish', r.grades); limitBoxes('symmetry', r.grades); }
    }

    // Sliders (carat / price / table / ratio) — each with editable min/max fields
    const d = defaultState();
    const carat = makeRange({
      mode: 'num', min: d.caratMin, max: d.caratMax, step: 0.1,
      lo: state.caratMin, hi: state.caratMax,
      editable: true, unit: 'ct', decimals: 2,
      onInput: (lo, hi) => { state.caratMin = lo; state.caratMax = hi; },
      onCommit: fire
    });
    const price = makeRange({
      mode: 'num', min: d.priceMin, max: d.priceMax,
      step: Math.max(100, Math.round((d.priceMax - d.priceMin) / 200 / 100) * 100),
      lo: state.priceMin, hi: state.priceMax,
      editable: true, prefix: '$', decimals: 0,
      onInput: (lo, hi) => { state.priceMin = lo; state.priceMax = hi; },
      onCommit: fire
    });
    const tableR = makeRange({
      mode: 'num', min: d.tableMin, max: d.tableMax, step: 1,
      lo: state.tableMin, hi: state.tableMax,
      editable: true, unit: '%', decimals: 0,
      onInput: (lo, hi) => { state.tableMin = lo; state.tableMax = hi; },
      onCommit: fire
    });
    const ratio = makeRange({
      mode: 'num', min: d.ratioMin, max: d.ratioMax, step: 0.01,
      lo: state.ratioMin, hi: state.ratioMax,
      editable: true, decimals: 2,
      onInput: (lo, hi) => { state.ratioMin = lo; state.ratioMax = hi; },
      onCommit: fire
    });
    container.querySelector('[data-slot="carat"]').appendChild(carat.el);
    container.querySelector('[data-slot="price"]').appendChild(price.el);
    container.querySelector('[data-slot="table"]').appendChild(tableR.el);
    container.querySelector('[data-slot="ratio"]').appendChild(ratio.el);

    function sync() {
      // Push (reset) state back into every control.
      seg.querySelectorAll('button').forEach(b => b.classList.toggle('is-on', b.dataset.val === state.type));
      shapesWrap.querySelectorAll('.dchip').forEach(b => b.classList.toggle('is-on', state.shapes.has(b.dataset.shape)));
      applyOriginConstraints();
      boxGroups.forEach(g => g.wrap.querySelectorAll('.dbox').forEach(b => b.classList.toggle('is-on', state[g.key].has(b.dataset.v))));
      carat.set(state.caratMin, state.caratMax);
      price.set(state.priceMin, state.priceMax);
      tableR.set(state.tableMin, state.tableMax);
      ratio.set(state.ratioMin, state.ratioMax);
    }
    applyOriginConstraints();   // initial state
    return { sync };
  }

  /* ---- Inject shared CSS (slider + quick-view) ------------------------ */
  function injectCSS() {
    if (document.getElementById('dfeed-css')) return;
    const css = document.createElement('style');
    css.id = 'dfeed-css';
    css.textContent = SHARED_CSS;
    document.head.appendChild(css);
  }

  /* ---- Public API ----------------------------------------------------- */
  return {
    SHAPES, SHAPE_NAME, COLOR, CLARITY, CUT, FLUOR, GRADE, CERTS,
    fmtUSD, fmtUSD0, fmtCt, titleCase, shapeIcon, shapeLabel, cutLabel,
    defaultState, serializeState, fetchFeed, fetchCount, countActive, describeChips, mediaHTML, fit360,
    applyURLParams, addCTALabel,
    makeRange, buildFilters, config: CFG,
    quickView: () => ensureQuickView(),
    init: injectCSS
  };

  /* ===================================================================== */
})();
